import { useRef, useState } from "react";
import type {
  BankQuestionDetail,
  QuestionInput,
  QuestionType,
} from "../../shared/types";
import { OPTION_KEYS, mediaUrl } from "../../shared/format";
import { OptGlyph } from "./motifs";
import { Ico } from "./shell";
import { api, ApiError } from "../api";
import { alertDialog } from "./Dialog";

export interface DraftOption {
  text: string;
  correct: boolean;
  imageKey?: string;
}
export interface DraftQuestion {
  type: QuestionType;
  prompt: string;
  category: string;
  chefNote: string;
  timeLimit: number;
  points: number;
  imageKey?: string;
  options: DraftOption[];
}

export const TIMER_CHOICES = [10, 15, 20, 25, 30];
export const POINTS_CHOICES = [500, 1000, 2000];

export function blankQuestion(): DraftQuestion {
  return {
    type: "mc",
    prompt: "",
    category: "",
    chefNote: "",
    timeLimit: 20,
    points: 1000,
    options: [
      { text: "", correct: true },
      { text: "", correct: false },
      { text: "", correct: false },
      { text: "", correct: false },
    ],
  };
}

export function questionIssue(q: DraftQuestion): string | null {
  if (!q.prompt.trim()) return "needs a question";
  const filled = q.options.filter((o) => o.text.trim());
  if (filled.length < 2) return "needs ≥2 answers";
  if (!q.options.some((o) => o.correct && o.text.trim())) return "needs a correct answer";
  return null;
}

/** Map a saved bank question into an editable draft. */
export function bankToDraft(b: BankQuestionDetail): DraftQuestion {
  return {
    type: b.type,
    prompt: b.prompt,
    category: b.category ?? "",
    chefNote: b.chefNote ?? "",
    timeLimit: b.timeLimit,
    points: b.points,
    imageKey: b.imageKey,
    options: b.options.map((o) => ({ text: o.text, correct: o.correct, imageKey: o.imageKey })),
  };
}

/** Map a draft into the QuestionInput payload (trimmed) for create/update. */
export function draftToInput(q: DraftQuestion): QuestionInput {
  return {
    type: q.type,
    prompt: q.prompt.trim(),
    category: q.category.trim() || undefined,
    chefNote: q.chefNote.trim() || undefined,
    timeLimit: q.timeLimit,
    points: q.points,
    imageKey: q.imageKey,
    options: q.options.map((o) => ({ text: o.text.trim(), correct: o.correct, imageKey: o.imageKey })),
  };
}

// ── Pure mutators: (q) => DraftQuestion ───────────────────────
export function applyType(qq: DraftQuestion, type: QuestionType): DraftQuestion {
  if (type === "truefalse") {
    const trueWasCorrect = qq.options[0]?.correct ?? true;
    return {
      ...qq,
      type,
      options: [
        { text: "True", correct: trueWasCorrect },
        { text: "False", correct: !trueWasCorrect },
      ],
    };
  }
  let opts = qq.options.filter((_, i) => i < 4);
  while (opts.length < 2) opts.push({ text: "", correct: false });
  if (type === "mc") {
    let seen = false;
    opts = opts.map((o) => {
      if (o.correct && !seen) {
        seen = true;
        return o;
      }
      return { ...o, correct: false };
    });
    if (!seen && opts[0]) opts[0] = { ...opts[0], correct: true };
  }
  return { ...qq, type, options: opts };
}

export function toggleCorrect(qq: DraftQuestion, i: number): DraftQuestion {
  if (qq.type === "multi") {
    return { ...qq, options: qq.options.map((o, j) => (j === i ? { ...o, correct: !o.correct } : o)) };
  }
  return { ...qq, options: qq.options.map((o, j) => ({ ...o, correct: j === i })) };
}

export function setOptionText(qq: DraftQuestion, i: number, text: string): DraftQuestion {
  return { ...qq, options: qq.options.map((o, j) => (j === i ? { ...o, text } : o)) };
}

export function addOption(qq: DraftQuestion): DraftQuestion {
  return qq.options.length >= 4 || qq.type === "truefalse"
    ? qq
    : { ...qq, options: [...qq.options, { text: "", correct: false }] };
}

export function removeOption(qq: DraftQuestion, i: number): DraftQuestion {
  if (qq.options.length <= 2 || qq.type === "truefalse") return qq;
  const options = qq.options.filter((_, j) => j !== i);
  if (qq.type === "mc" && !options.some((o) => o.correct)) options[0].correct = true;
  return { ...qq, options };
}

export function moveOption(qq: DraftQuestion, from: number, to: number): DraftQuestion {
  if (from === to || from < 0 || to < 0 || from >= qq.options.length || to >= qq.options.length) return qq;
  const options = [...qq.options];
  const [moved] = options.splice(from, 1);
  options.splice(to, 0, moved);
  return { ...qq, options };
}

/** The per-question editing form, driven by a draft + onChange. */
export function QuestionForm({
  question: q,
  onChange,
}: {
  question: DraftQuestion;
  onChange: (q: DraftQuestion) => void;
}) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const canDrag = q.type !== "truefalse";
  const segBox = (active: boolean, tone: "accent" | "gold"): React.CSSProperties => ({
    flex: 1,
    textAlign: "center",
    padding: "10px 0",
    fontSize: 13.5,
    fontWeight: 700,
    borderRadius: 9,
    cursor: "pointer",
    background: active ? (tone === "accent" ? "#fbf2f3" : "#fbf5e6") : "var(--atl-surface)",
    color: active ? (tone === "accent" ? "var(--atl-accent)" : "#8a6a23") : "var(--atl-soft)",
    border: `1px solid ${active ? (tone === "accent" ? "#e2c5ca" : "#e7d6ac") : "var(--atl-line)"}`,
  });

  return (
    <>
      <div>
        <div className="atl-eyebrow" style={{ marginBottom: 11 }}>QUESTION TYPE</div>
        <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
          {([["mc", "Multiple choice", "build"], ["truefalse", "True / false", "check"], ["multi", "Multi-select", "filter"]] as const).map(([t, label, ic]) => {
            const on = q.type === t;
            return (
              <div
                key={t}
                className="atl-chip"
                onClick={() => onChange(applyType(q, t))}
                style={{
                  fontSize: 13,
                  padding: "9px 14px",
                  cursor: "pointer",
                  background: on ? "var(--atl-ink)" : "var(--atl-surface)",
                  color: on ? "#fff" : "var(--atl-soft)",
                  borderColor: on ? "var(--atl-ink)" : "var(--atl-line)",
                }}
              >
                <Ico name={ic} size={14} /> {label}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="atl-eyebrow" style={{ marginBottom: 10 }}>THE QUESTION</div>
        <textarea
          className="atl-textarea atl-serif"
          rows={2}
          style={{ fontSize: 22, lineHeight: 1.25 }}
          value={q.prompt}
          onChange={(e) => onChange({ ...q, prompt: e.target.value })}
          placeholder="What do you want to ask the team?"
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 11 }}>
          <span className="atl-eyebrow">QUESTION IMAGE</span>
          <ImagePicker imageKey={q.imageKey} onChange={(key) => onChange({ ...q, imageKey: key })} />
          <span style={{ fontSize: 12, color: "var(--atl-faint)", fontWeight: 500 }}>Optional · shown on the question screen</span>
        </div>
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 11 }}>
          <span className="atl-eyebrow">
            ANSWER OPTIONS · MARK {q.type === "multi" ? "ALL CORRECT" : "THE CORRECT ONE"}
          </span>
          {q.type !== "truefalse" && q.options.length < 4 && (
            <button className="atl-btn atl-btn--link" style={{ marginLeft: "auto", fontSize: 12.5 }} onClick={() => onChange(addOption(q))}>
              + Add option
            </button>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }}>
          {q.options.map((o, i) => {
            const k = OPTION_KEYS[i] ?? "a";
            return (
              <div
                key={i}
                onDragOver={(e) => {
                  if (dragIdx === null) return;
                  e.preventDefault();
                  if (overIdx !== i) setOverIdx(i);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIdx !== null && dragIdx !== i) onChange(moveOption(q, dragIdx, i));
                  setDragIdx(null);
                  setOverIdx(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 13,
                  padding: "13px 15px",
                  borderRadius: 12,
                  background: "var(--atl-surface)",
                  border: `1px solid ${o.correct ? "var(--atl-c)" : "var(--atl-line)"}`,
                  boxShadow: o.correct ? "0 0 0 3px #eef2e7" : "none",
                  outline: overIdx === i && dragIdx !== null && dragIdx !== i ? "2px dashed var(--atl-accent)" : "none",
                  outlineOffset: 2,
                  opacity: dragIdx === i ? 0.5 : 1,
                }}
              >
                {canDrag && (
                  <span
                    draggable
                    onDragStart={() => setDragIdx(i)}
                    onDragEnd={() => {
                      setDragIdx(null);
                      setOverIdx(null);
                    }}
                    title="Drag to reorder"
                    style={{
                      flex: "0 0 auto",
                      cursor: "grab",
                      color: "var(--atl-faint)",
                      fontSize: 15,
                      lineHeight: 1,
                      userSelect: "none",
                      padding: "0 2px",
                    }}
                  >
                    ⠿
                  </span>
                )}
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: `var(--atl-${k})`,
                    display: "grid",
                    placeItems: "center",
                    flex: "0 0 auto",
                    fontFamily: '"Sprig", serif',
                    fontWeight: 600,
                    fontSize: 15,
                    color: "#fff",
                  }}
                >
                  {k.toUpperCase()}
                </span>
                <input
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    outline: "none",
                    padding: "4px 0",
                    fontWeight: 500,
                    fontSize: 14.5,
                    background: "transparent",
                    fontFamily: "var(--atl-sans)",
                    color: "var(--atl-ink)",
                  }}
                  value={o.text}
                  disabled={q.type === "truefalse"}
                  onChange={(e) => onChange(setOptionText(q, i, e.target.value))}
                  placeholder={`Option ${k.toUpperCase()}`}
                />
                {q.type !== "truefalse" && (
                  <ImagePicker
                    compact
                    imageKey={o.imageKey}
                    onChange={(key) =>
                      onChange({ ...q, options: q.options.map((oo, j) => (j === i ? { ...oo, imageKey: key } : oo)) })
                    }
                  />
                )}
                {q.type !== "truefalse" && q.options.length > 2 && (
                  <span title="Remove" onClick={() => onChange(removeOption(q, i))} style={{ color: "var(--atl-faint)", cursor: "pointer", fontWeight: 700, fontSize: 15, flex: "0 0 auto" }}>
                    ✕
                  </span>
                )}
                <span
                  onClick={() => onChange(toggleCorrect(q, i))}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    flex: "0 0 auto",
                    cursor: "pointer",
                    display: "grid",
                    placeItems: "center",
                    background: o.correct ? "var(--atl-c)" : "transparent",
                    color: "#fff",
                    border: o.correct ? "none" : "1.5px solid var(--atl-line)",
                  }}
                >
                  {o.correct && <Ico name="check" size={13} />}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 170 }}>
          <div className="atl-eyebrow" style={{ marginBottom: 9 }}>TIMER</div>
          <div style={{ display: "flex", gap: 6 }}>
            {TIMER_CHOICES.map((t) => (
              <div key={t} className="atl-mono" onClick={() => onChange({ ...q, timeLimit: t })} style={segBox(q.timeLimit === t, "accent")}>
                {t}s
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 170 }}>
          <div className="atl-eyebrow" style={{ marginBottom: 9 }}>POINTS</div>
          <div style={{ display: "flex", gap: 6 }}>
            {POINTS_CHOICES.map((p) => (
              <div key={p} className="atl-mono" onClick={() => onChange({ ...q, points: p })} style={segBox(q.points === p, "gold")}>
                {p.toLocaleString()}
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 170 }}>
          <div className="atl-eyebrow" style={{ marginBottom: 9 }}>CATEGORY</div>
          <input
            className="atl-tf"
            value={q.category}
            onChange={(e) => onChange({ ...q, category: e.target.value })}
            placeholder="e.g. Food Safety"
          />
        </div>
      </div>

      <div>
        <div className="atl-eyebrow" style={{ marginBottom: 9 }}>NOTE · SHOWN ON REVEAL</div>
        <textarea
          className="atl-textarea"
          rows={2}
          value={q.chefNote}
          onChange={(e) => onChange({ ...q, chefNote: e.target.value })}
          placeholder="A quick tip explaining the right answer."
        />
      </div>
    </>
  );
}

export function ImagePicker({
  imageKey,
  onChange,
  compact,
}: {
  imageKey?: string;
  onChange: (key: string | undefined) => void;
  compact?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function pick(file: File) {
    setBusy(true);
    try {
      const r = await api.upload(file);
      onChange(r.key);
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept="image/png,image/jpeg,image/webp,image/gif"
      style={{ display: "none" }}
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) pick(f);
        e.target.value = "";
      }}
    />
  );

  if (imageKey) {
    const size = compact ? 30 : 64;
    return (
      <span style={{ position: "relative", display: "inline-flex", flex: "0 0 auto" }}>
        {input}
        <img src={mediaUrl(imageKey)} alt="" style={{ width: size, height: size, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} />
        <button
          type="button"
          onClick={() => onChange(undefined)}
          title="Remove image"
          style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "var(--red)", color: "#fff", fontSize: 11, fontWeight: 800, lineHeight: 1, display: "grid", placeItems: "center", boxShadow: "var(--sh-sm)" }}
        >
          ✕
        </button>
      </span>
    );
  }
  return (
    <span style={{ flex: "0 0 auto" }}>
      {input}
      {compact ? (
        <button
          type="button"
          className="bk-btn bk-btn--ghost"
          title="Add answer image"
          style={{ width: 32, height: 32, padding: 0, borderRadius: 9, flex: "0 0 auto" }}
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? "…" : <Ico name="image" size={16} />}
        </button>
      ) : (
        <button type="button" className="bk-btn bk-btn--ghost" style={{ padding: "8px 14px", fontSize: 13 }} disabled={busy} onClick={() => inputRef.current?.click()}>
          <Ico name="plus" size={14} /> {busy ? "Uploading…" : "Add image"}
        </button>
      )}
    </span>
  );
}
